import { useQuery } from '@tanstack/react-query';
import { backend } from '@/api/backend';
import { ApiError } from '@/api/http';
import { explainLine } from '@/api/explain';
import { Stamp } from '@/components/ui';
import { toolLabel } from '@/lib/toolLabel';
import { say } from '@/lib/vocabulary';
import { counts, read, type Request } from '@/pages/reconstruction';

const retry = (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2);

const fmt = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

/**
 * What promotion actually set off.
 *
 * Confirming a detection enqueues a reconstruction on its own, attributed to
 * the reviewer who confirmed it. So an operator presses one button and a pass
 * runs somewhere with no further sign of it: for a whole release the product
 * could not say whether a promotion had produced records, produced none, or
 * been refused outright, because nothing over HTTP returned a request.
 *
 * Every row here is a request the backend wrote before it dispatched anything,
 * which is what makes a reconstruction that opened no run answerable rather
 * than silent.
 */
export function ReconstructionRequests({ bucketId }: { bucketId: string }) {
  const q = useQuery({
    queryKey: ['reconstruction-requests', bucketId],
    queryFn: ({ signal }) => backend.listReconstructionRequests(bucketId, signal),
    retry,
    // Queued and running are the states worth watching; anything terminal is
    // not going to change on its own.
    refetchInterval: (query) => {
      const items = (query.state.data?.items ?? []) as Request[];
      return items.some((r) => r.state === 'REQUESTED' || r.state === 'DISPATCHED') ? 10_000 : false;
    },
  });

  if (q.isPending) return <p className="px-4 py-3 text-small text-ink-3">Reading what reconstruction has been asked for.</p>;

  if (q.isError)
    return (
      <p role="alert" className="flex flex-wrap items-center gap-2 px-4 py-3 text-small text-warn">
        <span className="min-w-0 flex-1">{explainLine(q.error, 'read this bucket’s reconstructions')}</span>
        <button
          type="button"
          onClick={() => void q.refetch()}
          className="flex-none rounded border border-line bg-panel px-2.5 py-1 text-acc-2 transition-colors duration-fast hover:bg-hov"
        >
          Try again
        </button>
      </p>
    );

  const items = (q.data?.items ?? []) as Request[];
  if (items.length === 0)
    return (
      <p className="px-4 py-3 text-small text-ink-3">
        No reconstruction has been asked for on this scope. Confirming a detection asks for one, attributed to whoever
        confirmed it - unless nothing here can read that tool's export yet, in which case no request is queued rather
        than one that could only be refused.
      </p>
    );

  return (
    <div>
      {items.map((r) => {
        const state = read(r);
        const figures = counts(r);
        return (
          <div key={r.reconstructionRequestId} className="border-b border-line px-4 py-2.5 last:border-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <Stamp tone={state.tone}>{state.label}</Stamp>
              <span className="text-row text-ink">{toolLabel(r.toolSlug)}</span>
              <span className="text-caption text-ink-4">
                asked {fmt(r.requestedAt)}
                {r.completedAt ? ` · finished ${fmt(r.completedAt)}` : ''}
              </span>
              {/* A run is linked only where the request named one. REFUSED
                  never opened one, and sending someone to look for it is the
                  one mistake this surface exists to avoid. */}
              {state.hasRun && (
                <span className="mono ml-auto text-caption text-ink-3" title={r.reconstructionRunId ?? undefined}>
                  run {r.reconstructionRunId?.slice(0, 8)}
                </span>
              )}
            </div>
            <p className="mt-1 text-small text-ink-2">{state.means}</p>
            {/* The code was appended to that sentence and `say()` lowercases,
                so a failure read "...is not confirmed. no content reader
                grant." - the most specific fact on the row, arriving as a
                sentence fragment. It is the backend's own reason and it is
                labelled as one. */}
            {r.failureCode && (
              <p className="mt-1 text-small text-warn">
                <span className="text-ink-3">Reason given: </span>
                {say(r.failureCode)}
              </p>
            )}
            {figures.length > 0 && (
              <div className="mono mt-1 flex flex-wrap gap-x-4 gap-y-1 text-caption text-ink-3">
                {figures.map((f) => (
                  <span key={f.label}>
                    {f.value} <span className="text-ink-4">{f.label}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
